import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiUrl, getCsrfToken } from "../../utils/apiHelper";
import PromptsResultModal from "./PromptsResultModal";

function RegulationCompliancePromptsTab() {
  const { t } = useTranslation("generate");
  const [trainingDataType, setTrainingDataType] = useState("file");
  const [ipTrainingData, setIpTrainingData] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [numOfPrompts, setNumOfPrompts] = useState("1");
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [promptsModal, setPromptsModal] = useState({
    open: false,
    type: "",
    message: "",
    data: undefined,
  });
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIpTrainingData(file);
      setError(null);
    }
  };

  const handleGenerateRegulationCompliancePrompts = async () => {
    if (trainingDataType === "file" && !ipTrainingData) {
      setError(t("errors.uploadJson"));
      return;
    }

    if (trainingDataType === "text" && !promptText.trim()) {
      setError(t("errors.enterTrainingPrompts"));
      return;
    }

    if (trainingDataType === "file" && ipTrainingData) {
      const allowedExtensions = [".json"];
      const fileExtension = ipTrainingData.name.toLowerCase().substring(ipTrainingData.name.lastIndexOf("."));
      if (!allowedExtensions.includes(fileExtension)) {
        setError(t("errors.jsonOnly"));
        return;
      }
    }

    if (!fileName.trim()) {
      setError(t("errors.enterFileName"));
      return;
    }

    const safeFileName = fileName.trim();

    setPromptsModal({ open: false, type: "", message: "", data: undefined });
    setLoading(true);
    setError(null);

    try {
      const csrfToken = await getCsrfToken();

      const formData = new FormData();
      formData.append("numOfPrompts", numOfPrompts);
      formData.append("opFileName", safeFileName);

      if (trainingDataType === "file") {
        formData.append("ipTrainingData", ipTrainingData);
      } else if (trainingDataType === "text") {
        const promptsArray = promptText.trim().split("\n").filter((line) => line.trim() !== "");
        const jsonData = { prompts: promptsArray };
        const jsonBlob = new Blob([JSON.stringify(jsonData, null, 2)], {
          type: "application/json",
        });
        const jsonFile = new File([jsonBlob], "training-prompts.json", {
          type: "application/json",
        });
        formData.append("ipTrainingData", jsonFile);
      }

      const response = await fetch(getApiUrl("/api/internal/test-files/generate/compliance-regulation-prompts/"), {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(t("errors.unexpectedRegulationPrompts"));
      }

      const apiSuccess = data && typeof data.success === "boolean" ? data.success : response.ok;
      const apiMessage = apiSuccess
        ? (data && data.message) || t("success.regulationPrompts")
        : (data && data.detail) || t("errors.httpStatus", { status: response.status });

      if (!apiSuccess) {
        throw new Error(apiMessage);
      }

      setPromptsModal({
        open: true,
        type: "success",
        message: apiMessage,
        data: data.data,
      });
      setSelectedCategory(null);
    } catch (err) {
      console.error("Error generating regulation compliance prompts:", err);
      const message = err instanceof Error && err.message
        ? err.message
        : t("errors.unexpectedRegulationPromptsGeneric");
      setPromptsModal({
        open: true,
        type: "error",
        message,
      });
      setSelectedCategory(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="generate-form-container">
        <div className="generate-form-grid">
          <aside className="generate-disclaimer generate-form-group-full" role="note">
            <strong className="generate-disclaimer-label">{t("common.note")}</strong>{" "}
            {t("regulationCompliance.disclaimer")}
          </aside>

          <div className="generate-form-section generate-form-group-full">
            <div className="generate-section-header">
              <h4 className="generate-section-title">{t("common.trainingData")}</h4>
              <p className="generate-section-description">
                {t("regulationCompliance.trainingDataDesc")}
              </p>
            </div>

            <div className="generate-form-group">
              <label className="generate-form-label">{t("common.trainingDataType")}</label>
              <p className="generate-field-description">
                {t("common.trainingDataTypeDesc")}
              </p>
              <div className="generate-radio-group">
                <label className={`generate-radio-option ${trainingDataType === "file" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="regTrainingDataType"
                    value="file"
                    checked={trainingDataType === "file"}
                    onChange={(e) => setTrainingDataType(e.target.value)}
                  />
                  <span className="generate-radio-label">{t("common.uploadJsonFile")}</span>
                </label>
                <label className={`generate-radio-option ${trainingDataType === "text" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="regTrainingDataType"
                    value="text"
                    checked={trainingDataType === "text"}
                    onChange={(e) => setTrainingDataType(e.target.value)}
                  />
                  <span className="generate-radio-label">{t("common.enterPrompts")}</span>
                </label>
              </div>
            </div>

            {trainingDataType === "file" && (
              <div className="generate-form-group generate-file-upload-group">
                <div className="generate-file-upload-container">
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleFileUpload}
                    id="reg-training-data-upload"
                  />
                  <label htmlFor="reg-training-data-upload" className="generate-file-upload-label">
                    {ipTrainingData ? (
                      <div className="generate-file-upload-success">
                        <div className="generate-file-upload-icon"></div>
                        <div className="generate-file-upload-content">
                          <div className="generate-file-upload-title">{t("common.fileSelected")}</div>
                          <div className="generate-file-upload-name">{ipTrainingData.name}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="generate-file-upload-placeholder">
                        <div className="generate-file-upload-icon"></div>
                        <div className="generate-file-upload-content">
                          <div className="generate-file-upload-title">{t("common.clickToUploadJson")}</div>
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            )}

            {trainingDataType === "text" && (
              <div className="generate-form-group generate-enter-prompts-group">
                <label className="generate-form-label">{t("common.enterPrompts")}</label>
                <textarea
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  placeholder={t("common.enterPromptsPlaceholder")}
                  className="generate-form-textarea"
                  rows="6"
                />
              </div>
            )}
          </div>

          <div className="generate-form-section generate-form-group-full">
            <div className="generate-section-header">
              <h4 className="generate-section-title">{t("common.generatedFileOptions")}</h4>
              <p className="generate-section-description">
                {t("common.chooseHowManyPrompts")}
              </p>
            </div>

            <div className="generate-output-grid">
              <div className="generate-form-group">
                <label className="generate-form-label">{t("common.fileName")}</label>
                <input
                  type="text"
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  className="generate-form-input"
                  placeholder={t("common.fileNamePlaceholder")}
                  required
                />
              </div>

              <div className="generate-form-group">
                <label className="generate-form-label">{t("common.numPrompts")}</label>
                <input
                  type="number"
                  value={numOfPrompts}
                  onChange={(e) => setNumOfPrompts(e.target.value)}
                  onBlur={(e) => {
                    const value = parseInt(e.target.value);
                    if (!value || value < 1) {
                      setNumOfPrompts("1");
                    }
                  }}
                  min="1"
                  max="1000"
                  className="generate-form-input"
                  placeholder={t("common.numPromptsPlaceholder")}
                />
              </div>
            </div>
          </div>

          <div className="generate-form-group generate-form-group-full">
            <button
              onClick={handleGenerateRegulationCompliancePrompts}
              disabled={loading || !fileName.trim() || (!ipTrainingData && !promptText.trim())}
              className="generate-form-button"
            >
              {loading ? (
                <>
                  <div className="generate-button-spinner"></div>
                  {t("common.generating")}
                </>
              ) : (
                <>{t("regulationCompliance.generate")}</>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="generate-status-message generate-status-error">
            <div className="generate-status-icon"></div>
            <div className="generate-status-content">{t("common.error", { message: error })}</div>
          </div>
        )}
      </div>

      <PromptsResultModal
        modal={promptsModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        keyPrefix="reg-"
        onClose={() => {
          setPromptsModal({ open: false, type: "", message: "", data: undefined });
          setSelectedCategory(null);
        }}
      />
    </>
  );
}

export default RegulationCompliancePromptsTab;
